import {
  Component, Input, Output, EventEmitter,
  AfterViewInit, OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef
} from '@angular/core';
import { Editor, Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Asset } from '@shared/models/asset.model';
import { AssetType } from '@shared/models/assetType.model';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { UploadService } from '@shared/services/upload.service';

const TabIndent = Extension.create({
  name: 'tabIndent',
  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        if (editor.isActive('listItem')) return false;
        return editor.commands.insertContent('    ');
      },
    };
  },
});

@Component({
  selector: 'rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss']
})
export class RichTextEditorComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('editorEl') editorEl!: ElementRef<HTMLElement>;
  @ViewChild('linkInputEl') linkInputEl?: ElementRef<HTMLInputElement>;

  /** Initial HTML content and subsequent updates. Editor only resets if the new value differs from current HTML. */
  @Input() content = '';
  /** Controls whether the editor is editable. Can be toggled at runtime. */
  @Input() editable = false;
  /** Course ID passed to the file manager and used for direct file uploads. */
  @Input() courseId: string;
  /** Maximum number of images allowed in the editor. Defaults to unlimited. */
  @Input() maxImages = Infinity;

  /** Emits the editor's HTML on every blur event. */
  @Output() contentChange = new EventEmitter<string>();

  editor!: Editor;
  showLinkInput = false;
  linkUrl = '';
  isUploading = false;
  rteError: string | null = null;

  private _errorTimer: any;

  constructor(
    private _uploadService: UploadService,
    private _dialog: MatDialog
  ) {}

  ngAfterViewInit(): void {
    // Captured here so NodeView closures (regular functions) can reach component methods.
    // Arrow functions in editorProps/callbacks can use 'this' directly.
    const self = this;

    // Extends Image with a custom attribute (data-asset-id) and a NodeView that adds
    // a hover-visible delete button and click-to-replace behaviour directly on the image.
    const imageExtension = Image.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          assetId: {
            default: null,
            parseHTML: element => element.getAttribute('data-asset-id'),
            renderHTML: attributes => {
              if (!attributes.assetId) return {};
              return { 'data-asset-id': attributes.assetId };
            },
          },
        };
      },
      addNodeView() {
        return ({ node: initialNode, editor, getPos }) => {
          let node = initialNode;

          const dom = document.createElement('div');
          dom.classList.add('rte-image-wrapper');

          const img = document.createElement('img');
          img.src = node.attrs['src'] ?? '';
          if (node.attrs['alt']) img.alt = node.attrs['alt'];
          if (node.attrs['assetId']) img.setAttribute('data-asset-id', node.attrs['assetId']);
          // Click image → open file manager to replace
          img.addEventListener('click', () => {
            if (!editor.isEditable) return;
            self._openMediaPicker((asset) => {
              if (typeof getPos !== 'function') return;
              const tr = editor.state.tr.setNodeMarkup(getPos(), null, {
                ...node.attrs, src: asset.path, assetId: asset.id,
              });
              editor.view.dispatch(tr);
            });
          });

          const deleteBtn = document.createElement('button');
          deleteBtn.classList.add('rte-image-delete');
          deleteBtn.type = 'button';
          deleteBtn.title = 'Remove image';
          const icon = document.createElement('i');
          icon.className = 'fa fa-trash';
          deleteBtn.appendChild(icon);
          // mousedown (not click) so the editor keeps focus while we delete
          deleteBtn.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof getPos !== 'function') return;
            const pos = getPos();
            editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
          });

          dom.appendChild(img);
          dom.appendChild(deleteBtn);

          return {
            dom,
            update(updatedNode) {
              if (updatedNode.type.name !== 'image') return false;
              node = updatedNode;
              img.src = updatedNode.attrs['src'] ?? '';
              if (updatedNode.attrs['assetId']) {
                img.setAttribute('data-asset-id', updatedNode.attrs['assetId']);
              } else {
                img.removeAttribute('data-asset-id');
              }
              return true;
            },
          };
        };
      },
    });

    this.editor = new Editor({
      element: this.editorEl.nativeElement,
      extensions: [
        StarterKit.configure({ heading: false }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
        }),
        imageExtension,
        TabIndent,
      ],
      editorProps: {
        handleDrop: (_view, event: DragEvent, _slice, moved) => {
          if (!moved && event.dataTransfer?.files?.length) {
            const file = event.dataTransfer.files[0];
            if (file.type.startsWith('image/')) {
              if (this.atImageLimit) {
                this._showError(this._imageLimitMessage());
              } else {
                this._uploadAndInsertFile(file);
              }
            } else {
              this._showError('Only image files can be inserted (JPG, PNG, GIF, WebP…)');
            }
            return true; // always consume so browsers don't try to navigate to the file
          }
          return false;
        },
        handlePaste: (_view, event: ClipboardEvent) => {
          const items = Array.from(event.clipboardData?.items ?? []);
          const fileItem = items.find(i => i.kind === 'file');
          if (fileItem) {
            if (fileItem.type.startsWith('image/')) {
              if (this.atImageLimit) {
                this._showError(this._imageLimitMessage());
                return true;
              }
              const file = fileItem.getAsFile();
              if (file) {
                this._uploadAndInsertFile(file);
                return true;
              }
            } else {
              this._showError('Only image files can be inserted (JPG, PNG, GIF, WebP…)');
              return true;
            }
          }
          // Block HTML pastes that contain images (e.g. copy-paste from within the editor)
          if (this.atImageLimit) {
            const html = event.clipboardData?.getData('text/html') ?? '';
            if (html.includes('<img')) {
              this._showError(this._imageLimitMessage());
              return true;
            }
          }
          return false;
        },
      },
      content: this.content,
      editable: this.editable,
      onBlur: ({ editor }) => {
        this.contentChange.emit(editor.getHTML());
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['content'] && !changes['content'].firstChange && this.editor) {
      const incoming = changes['content'].currentValue ?? '';
      if (this.editor.getHTML() !== incoming) {
        this.editor.commands.setContent(incoming);
      }
    }
    if (changes['editable'] && !changes['editable'].firstChange && this.editor) {
      this.editor.setEditable(this.editable);
    }
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
    clearTimeout(this._errorTimer);
  }

  get isEditable(): boolean {
    return this.editor?.isEditable ?? false;
  }

  get imageCount(): number {
    if (!this.editor) return 0;
    let count = 0;
    this.editor.state.doc.descendants(node => { if (node.type.name === 'image') count++; });
    return count;
  }

  get atImageLimit(): boolean {
    return this.imageCount >= this.maxImages;
  }

  openImagePicker(): void {
    if (this.atImageLimit) return;
    this._openMediaPicker((asset) => {
      this.editor.chain().focus().setImage({ src: asset.path, assetId: asset.id } as any).run();
    });
  }

  toggleBold(): void {
    this.editor.chain().focus().toggleBold().run();
  }

  toggleItalic(): void {
    this.editor.chain().focus().toggleItalic().run();
  }

  toggleBulletList(): void {
    this.editor.chain().focus().toggleBulletList().run();
  }

  toggleOrderedList(): void {
    this.editor.chain().focus().toggleOrderedList().run();
  }

  isActive(mark: string): boolean {
    return this.editor?.isActive(mark) ?? false;
  }

  openLinkInput(): void {
    if (this.isActive('link')) {
      this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    this.linkUrl = this.editor.getAttributes('link')['href'] ?? '';
    this.showLinkInput = true;
    setTimeout(() => this.linkInputEl?.nativeElement?.focus(), 0);
  }

  applyLink(): void {
    const url = this.linkUrl.trim();
    if (url) {
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    this.showLinkInput = false;
    this.linkUrl = '';
  }

  cancelLink(): void {
    this.showLinkInput = false;
    this.linkUrl = '';
  }

  private _imageLimitMessage(): string {
    return `Maximum of ${this.maxImages} image${this.maxImages !== 1 ? 's' : ''} allowed here`;
  }

  private _showError(msg: string): void {
    this.rteError = msg;
    clearTimeout(this._errorTimer);
    this._errorTimer = setTimeout(() => { this.rteError = null; }, 3000);
  }

  /** Prevents default on dragover so the browser allows a drop on the entire editor area. */
  onEditorDragOver(event: DragEvent): void {
    if (this.editable && event.dataTransfer?.types?.includes('Files')) {
      event.preventDefault();
    }
  }

  _openMediaPicker(onSelect: (asset: Asset) => void): void {
    this._dialog.open(FileManagerComponent, {
      panelClass: 'learnspace-theme',
      data: { title: 'Select image', type: AssetType.i2D, courseId: this.courseId },
      id: 'file-manager'
    }).afterClosed().subscribe((asset: Asset) => {
      if (asset) onSelect(asset);
    });
  }

  private _uploadAndInsertFile(file: File): void {
    if (this.atImageLimit) return;
    const asset = new Asset(AssetType.i2D, file.name, undefined, file.name);
    this.isUploading = true;
    this._uploadService.createAndUploadAsset(asset, file, this.courseId)
      .subscribe({
        next: (uploaded) => {
          this.editor.chain().focus().setImage({ src: uploaded.path, assetId: uploaded.id } as any).run();
          this.isUploading = false;
          // Generate thumbnail asynchronously so the asset shows a preview in the file manager
          const canvas = document.createElement('canvas');
          this._uploadService.optimizeThumbnail(file, uploaded, canvas);
        },
        error: () => {
          this.isUploading = false;
        }
      });
  }
}
